import React, { useEffect, useRef } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  width?: string | number;
  height?: string | number;
  closeOnOverlayClick?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  size = 'medium',
  width,
  height,
  closeOnOverlayClick = true
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === overlayRef.current) {
      onClose();
    }
  };

  // Handle modal click to prevent closing when clicking inside modal
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (!isOpen) return null;

  // Get modal size class
  const getModalSizeClass = () => {
    if (width) return 'idto_digilocker_modal_custom';
    switch (size) {
      case 'small': return 'idto_digilocker_modal_small';
      case 'medium': return 'idto_digilocker_modal_medium';
      case 'large': return 'idto_digilocker_modal_large';
      default: return 'idto_digilocker_modal_medium';
    }
  };

  // Get modal dimensions
  const modalDimensions = {
    width: width || undefined,
    height: height || '560px'
  };

  return (
    <div
      ref={overlayRef}
      className="idto_digilocker_modal_overlay"
      onClick={handleOverlayClick}
    >
      <div
        ref={modalRef}
        className={`idto_digilocker_modal_content ${getModalSizeClass()}`}
        style={modalDimensions}
        onClick={handleModalClick}
      >
        {children}
      </div>
    </div>
  );
};

export default Modal;
